import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { Location } from '@core/models/common.model';
import { OrganizationContextService } from '@core/services/organization-context.service';
import { LocationService } from '../../modules/organization/services/location.service';

/** The location the user is working in. Pages read `selected` instead of asking the user again. */
@Injectable({ providedIn: 'root' })
export class LocationContextService {
  private static readonly CACHE_KEY = 'clis.location-context';

  private readonly locationService = inject(LocationService);

  private readonly cached = this.readCache();
  private readonly locationsSubject = new BehaviorSubject<Location[]>(this.cached.locations);
  private readonly selectedSubject = new BehaviorSubject<Location | null>(
    this.cached.locations.find((l) => l.id === this.cached.selectedId) ?? this.cached.locations[0] ?? null,
  );

  readonly locations$ = this.locationsSubject.asObservable();
  readonly selected$ = this.selectedSubject.asObservable();

  constructor() {
    let hadTenant = false;
    inject(OrganizationContextService)
      .tenantId$.pipe(
        distinctUntilChanged(),
        tap((tenantId) => (hadTenant = hadTenant || !!tenantId)),
        switchMap((tenantId) =>
          tenantId ? this.locationService.getLocationsByOrganization(tenantId).pipe(catchError(() => of(undefined))) : of(null),
        ),
      )
      .subscribe((response) => {
        // no tenant: either the initial value before the session is hydrated (keep the cache) or a logout (clear it)
        if (response === null) return hadTenant ? this.reset() : undefined;
        // request failed: keep what we had (cache from the last visit) so the switcher doesn't vanish
        if (response === undefined || !response.success || !response.data) return;
        const locations = response.data;
        this.locationsSubject.next(locations);
        // keep the user's previous choice across refreshes; shortcut: first location until locations are assigned to users
        const keep = locations.find((l) => l.id === this.selectedSubject.value?.id);
        this.selectedSubject.next(keep ?? locations[0] ?? null);
        this.writeCache();
      });
  }

  get selected(): Location | null {
    return this.selectedSubject.value;
  }

  select(id: string): void {
    const location = this.locationsSubject.value.find((l) => l.id === id);
    if (location) {
      this.selectedSubject.next(location);
      this.writeCache();
    }
  }

  private reset(): void {
    this.locationsSubject.next([]);
    this.selectedSubject.next(null);
    try {
      localStorage.removeItem(LocationContextService.CACHE_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  private readCache(): { locations: Location[]; selectedId: string | null } {
    try {
      const parsed = JSON.parse(localStorage.getItem(LocationContextService.CACHE_KEY) ?? 'null');
      if (parsed && Array.isArray(parsed.locations)) return parsed;
    } catch {
      /* ignore a corrupt or unavailable cache */
    }
    return { locations: [], selectedId: null };
  }

  private writeCache(): void {
    try {
      localStorage.setItem(
        LocationContextService.CACHE_KEY,
        JSON.stringify({ locations: this.locationsSubject.value, selectedId: this.selectedSubject.value?.id ?? null }),
      );
    } catch {
      /* storage unavailable */
    }
  }
}
